import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Store, Bike, Truck } from "lucide-react";
import { api } from "@/lib/api";
import WineCard from "@/components/WineCard";
import SommelierQuiz from "@/components/SommelierQuiz";
import { LocationSection } from "@/components/LocationSection";
import GoogleReviews from "@/components/GoogleReviews";
import NewsletterSignup from "@/components/NewsletterSignup";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { FAQS } from "@/lib/faqs";
import { EditableText } from "@/components/Editable";

const HERO_IMG = "/enoteca-hero-1280.webp";
const HERO_SRCSET = [
  "/enoteca-hero-640.webp 640w",
  "/enoteca-hero-960.webp 960w",
  "/enoteca-hero-1280.webp 1280w",
  "/enoteca-hero.webp 1448w",
].join(", ");
const ENOTECA_IMG = "/enoteca-uvaspina.webp";

const heroLines = ["La Tua enoteca.", "Anche a casa."];

function HeroLine({ children, delay }) {
  return (
    <span className="line-mask">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const ORDER_METHODS = [
  {
    icon: Store,
    tag: "Gratis",
    title: "Ritiro in negozio",
    desc: "Ordina online e ritira comodamente in enoteca a Rovigo, scegliendo giorno e fascia oraria. Puoi anche pagare al ritiro.",
    to: "/shop",
    cta: "Ordina e ritira",
  },
  {
    icon: Bike,
    tag: "da 2,90 €",
    title: "Consegna a domicilio",
    desc: "Entro 5 km dall'enoteca: a casa o a chi vuoi, anche in giornata. Bianchi e bollicine anche consegnati freschi.",
    to: "/shop",
    cta: "Consegna a casa",
  },
  {
    icon: Truck,
    tag: "7,90 € · gratis oltre 80 €",
    title: "Spedizione in tutta Italia",
    desc: "Imballo protettivo e corriere tracciato. Il vino arriva in 2-4 giorni lavorativi, come è partito dalla cantina.",
    to: "/spedizioni",
    cta: "Scopri spedizioni",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [holiday, setHoliday] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    document.title = "UvaShop | Enoteca Uvaspina a Rovigo - Consegna Vini a Rovigo e in tutta Italia";
    const desc = "Enoteca Uvaspina a Rovigo: vini, bollicine, birre artigianali e distillati. Acquista online e scegli ritiro in enoteca, consegna a domicilio o spedizione.";
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", desc);
    api.get("/wines", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6)));
    api.get("/delivery/availability").then((r) => setHoliday(r.data)).catch(() => {});
  }, []);


  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden flex items-end">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO_IMG} srcSet={HERO_SRCSET} sizes="100vw" alt="Interno dell'Enoteca Uvaspina a Rovigo" width="1448" height="1086" fetchpriority="high" decoding="async" className="w-full h-full object-cover object-[22%_center] md:object-center" />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </motion.div>

        {holiday?.closure_active && holiday?.message && (
          <div data-testid="home-holiday-banner" className="absolute top-24 md:top-[104px] left-0 right-0 z-40 bg-claret/95 backdrop-blur-sm text-bone text-center px-6 py-4 text-sm sm:text-base leading-snug">
            {holiday.message}
          </div>
        )}

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-10 pb-20 lg:pb-28">
          <h1 className="font-serif-display text-bone text-[15vw] md:text-[9vw] leading-[0.92] font-500">
            {heroLines.map((l, i) => <HeroLine key={l} delay={0.4 + i * 0.15}>{l}</HeroLine>)}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10"
          >
            <Link
              to="/shop"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-bone text-ink px-8 py-4 uppercase tracking-[0.15em] text-sm hover:bg-claret hover:text-bone transition-colors"
            >
              Esplora il catalogo
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED — Cosa beviamo */}
      {featured.length > 0 && (
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-claret mb-4">Questa settimana</p>
            <EditableText id="home_featured_title" as="h2" className="font-serif-display text-4xl lg:text-6xl leading-[1.05]" defaultValue="Cosa beviamo? Le etichette della settimana." testid="home-featured-title" />
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 link-underline uppercase tracking-wide text-sm">
            Vedi tutti <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {featured.map((w, i) => <WineCard key={w.id} wine={w} index={i} />)}
        </div>
      </section>
      )}

      {/* SOMMELIER VIRTUALE — Quiz IA */}
      <SommelierQuiz />

      {/* MODALITÀ D'ORDINE — come ricevi il tuo vino */}
      <section className="bg-bone py-24 lg:py-32" data-testid="order-methods-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-claret mb-4">Come ricevi il tuo vino</p>
            <EditableText id="home_methods_title" as="h2" className="font-serif-display text-5xl lg:text-6xl leading-[1.05]" defaultValue="Tre modi per riceverlo." testid="home-methods-title" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {ORDER_METHODS.map((m, i) => (
              <motion.div
                key={m.title}
                data-testid={`order-method-${i}`}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="group relative flex flex-col bg-white border border-ink/10 p-8 lg:p-10 hover:border-claret transition-colors duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-linen text-ink group-hover:bg-claret group-hover:text-bone transition-colors duration-300 mb-8">
                  <m.icon size={28} strokeWidth={1.5} />
                </div>
                <span className="inline-block self-start text-[11px] uppercase tracking-[0.15em] text-claret border border-claret/30 px-3 py-1 mb-5">{m.tag}</span>
                <h3 className="font-serif-display text-2xl lg:text-3xl mb-3">{m.title}</h3>
                <p className="text-ink-soft leading-relaxed flex-1">{m.desc}</p>
                <Link to={m.to} className="mt-8 inline-flex items-center gap-2 uppercase tracking-[0.15em] text-sm text-ink group-hover:text-claret transition-colors">
                  {m.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ATMOSPHERE / CTA */}
      <section className="relative h-[70vh] overflow-hidden flex items-center">
        <img src={ENOTECA_IMG} alt="Interno enoteca" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/55" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-bone">
          <h2 className="font-serif-display text-5xl lg:text-7xl leading-[1.02] max-w-3xl">Vieni a trovarci, o lascia che il vino venga da te.</h2>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-3 border border-bone/40 hover:bg-bone hover:text-ink px-8 py-4 uppercase tracking-[0.15em] text-sm transition-colors">
            Ordina online <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <LocationSection />

      <section className="bg-bone py-24 lg:py-32" data-testid="home-faq-section">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-claret mb-4">Domande frequenti</p>
          <h2 className="font-serif-display text-4xl lg:text-6xl leading-none mb-10">Tutto quello che vuoi sapere.</h2>
          <Accordion type="single" collapsible className="border-t border-ink/12">
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`home-faq-${i}`} className="border-b border-ink/12">
                <AccordionTrigger data-testid={`home-faq-q-${i}`} className="text-left text-base lg:text-lg py-5 hover:text-claret">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed text-[15px] pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Link to="/faq" data-testid="home-faq-link" className="mt-8 inline-flex items-center gap-3 text-claret hover:text-claret-dark uppercase tracking-[0.15em] text-sm transition-colors">
            Vedi tutte le domande <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <GoogleReviews />

      <NewsletterSignup />
    </div>
  );
}
